import { useCallback, useEffect, useMemo } from 'react';
import { isNil, toString } from 'lodash';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { FUND_ALIAS } from 'common/constants/general';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useFundQueryParam } from 'hooks';
import { FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS } from 'pages/Firms/common/constants';
import { useGetFundListByFirm } from 'services/hooks/firms';
import { getObjectValue } from 'utilities';
import { UseFirmFundFilterReturn } from './useFirmFundFilter.types';

const useFirmFundFilter = (): UseFirmFundFilterReturn => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);

  const { data: fundList, isLoading: isLoadingFundList } = useGetFundListByFirm({
    firmId: currentFirmId,
    shouldQueryAutomatically: !isNil(currentFirmId),
  });

  const fundOptions = useMemo(() => fundList ?? [], [fundList]);

  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);
  const fundQueryParam = parsedQuery[FUND_ALIAS];

  const { setFundQueryParam } = useFundQueryParam();

  const selectedFundFromQuery = useMemo(() => {
    if (isNil(fundQueryParam) || fundQueryParam === '') {
      return null;
    }
    const paramAsId = Number(fundQueryParam);
    if (!Number.isNaN(paramAsId)) {
      return fundOptions.find(fund => fund.id === paramAsId) ?? null;
    }
    return null;
  }, [fundOptions, fundQueryParam]);

  const isAllFundsSelected = isNil(selectedFundFromQuery);

  const selectedFund = isAllFundsSelected ? FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS : selectedFundFromQuery.name;
  const selectedFundId = isAllFundsSelected ? undefined : selectedFundFromQuery.id;

  const setSelectedFund = useCallback(
    (fundIdOrAllFunds: string) => {
      if (fundIdOrAllFunds === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS) {
        setFundQueryParam({ fund: '' });
        return;
      }
      setFundQueryParam({ fund: toString(fundIdOrAllFunds) });
    },
    [setFundQueryParam]
  );

  // Clear the param when the user picked a specific fund that no longer exists
  // for the current measurement date (so a stale URL doesn't silently apply).
  useEffect(() => {
    if (isLoadingFundList) {
      return;
    }
    if (isNil(fundQueryParam) || fundQueryParam === '') {
      return;
    }
    if (selectedFundFromQuery === null) {
      setFundQueryParam({ fund: '' });
    }
  }, [
    fundQueryParam,
    isLoadingFundList,
    selectedFundFromQuery,
    setFundQueryParam,
  ]);

  return {
    fundOptions,
    isAllFundsSelected,
    isLoadingFunds: isLoadingFundList,
    selectedFund,
    selectedFundId,
    setSelectedFund,
  };
};

export default useFirmFundFilter;
